import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";

import { settingsAjax } from "../../ajax/SettingsAjax";
import { setShiftBoundaries, setShiftEditability, setShiftEditabilityDataLoading } from "../../state/reducers/shiftReducer";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { useTimezone } from "../Providers/TimezoneProvider";
import { RootState } from "../../state/store";
import { shiftEnd, shiftStart } from "../../offline/shiftBands";

export default function useShiftBoundarySync() {
  const dispatch = useDispatch();
  const { timezone, isTimezoneLoading } = useTimezone();
  const selectedDateStr = useSelector((state: RootState) => state.shift.selectedDate);
  const selectedShift = useSelector((state: RootState) => state.shift.selectedShift);
  const latestShiftDate = useSelector((state: RootState) => state.shift.latestShiftDate);
  const latestShift = useSelector((state: RootState) => state.shift.latestShift);
  const provisionalShiftDate = useSelector((state: RootState) => state.shift.provisionalShiftDate);
  const provisionalShift = useSelector((state: RootState) => state.shift.provisionalShift);
  const [shiftBoundaryFetchError, setShiftBoundaryFetchError] = useState<string | null>(null);
  const [shiftEditabilityFetchError, setShiftEditabilityFetchError] = useState<string | null>(null);

  useEffect(() => {
    // Note: Wait until timezone is fully loaded — without it, selectedDate would use a UTC
    // fallback and the effect would fire a second time when the real timezone arrives,
    // dispatching setShiftEditability twice and flashing the lock banner.
    if (!selectedDateStr || !selectedShift || isTimezoneLoading || !timezone) return;

    const selectedDate = DateTime.fromISO(selectedDateStr)
      .toLocal()
      .setZone(timezone, { keepLocalTime: true })
      .startOf("day");
    const backendCode = selectedShift === "DAY" ? "D" : "N";

    setShiftBoundaryFetchError(null);
    dispatch(setShiftEditabilityDataLoading(true));

    // Note: offline this request cannot be answered, and leaving the previous shift's boundaries in
    // place is worse than having none: every time picker keeps offering the old shift's hours (a
    // provisional day shift still ran 18:00 - 06:00). Derive them from the cached ShiftBands, which
    // is the same configuration the server computes from.
    const applyCachedBandBoundaries = () => {
      dispatch(setShiftBoundaries({
        start: shiftStart(selectedDateStr, selectedShift).toISO()!,
        end: shiftEnd(selectedDateStr, selectedShift).toISO()!
      }));
    };

    settingsAjax.getShiftBoundaries(selectedDate, backendCode)
      .then(res => {
        if (!ErrorResponse.isErrorResponse(res)) {
          dispatch(setShiftBoundaries({
            start: res.shiftStart.toISO()!,
            end: res.shiftEnd.toISO()!
          }));
          setShiftBoundaryFetchError(null);
        } else {
          applyCachedBandBoundaries();
          setShiftBoundaryFetchError(res.messages[0].description);
          console.error("Failed to fetch shift boundaries", res);
        }
      })
      .catch(error => {
        if (error !== "CANCELLED") {
          applyCachedBandBoundaries();
          setShiftBoundaryFetchError("Failed to fetch shift boundaries");
          console.error("Failed to fetch shift boundaries", error);
        }
      })
      .then(() => settingsAjax.getShiftEditability(selectedDate, backendCode)
        .then(res => {
          if (!ErrorResponse.isErrorResponse(res)) {
            dispatch(setShiftEditability(res));
            setShiftEditabilityFetchError(null);
          } else {
            setShiftEditabilityFetchError(res.messages[0].description);
            console.error("Failed to fetch shift editability", res);
          }
        })
        .catch(() => {
          setShiftEditabilityFetchError("Failed to fetch shift editability");
          // Note: offline the server can't tell us the lock state. Keep the persisted latest
          // shift editable so a cold offline reload does not lock out the crew working it, and
          // conservatively lock every other shift (offline we cannot verify the auto-lock window
          // or manual unlocks). The server re-checks on sync, so a wrongly-permitted edit to a
          // locked shift is still rejected there.
          // Note: a shift provisionally created by an offline completion is editable too - it does
          // not exist on the server yet, so nothing can report its lock state, and the crew has
          // just moved into it.
          const isLatestShift = (latestShiftDate === selectedDateStr && latestShift === selectedShift)
            || (provisionalShiftDate === selectedDateStr && provisionalShift === selectedShift);
          dispatch(setShiftEditability({
            isLocked: !isLatestShift,
            canToggleLock: false,
            isPastAutoLockCutOff: null
          }));
        })
      )
      .finally(() => {
        dispatch(setShiftEditabilityDataLoading(false));
      });
  }, [dispatch, selectedDateStr, selectedShift, latestShiftDate, latestShift, provisionalShiftDate, provisionalShift, isTimezoneLoading, timezone]);

  return { isTimezoneLoading, shiftBoundaryFetchError, shiftEditabilityFetchError };
}
